/**
 * Graph Search Component
 *
 * Search for nodes in a knowledge graph
 */
import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { knowledgeGraphStore } from '@/stores/KnowledgeGraphStore'
import { NodeType, GraphNode } from '@/types'
import toast from 'react-hot-toast'

interface GraphSearchProps {
  graphId: string
  onNodeSelect?: (node: GraphNode) => void
}

const GraphSearch: React.FC<GraphSearchProps> = observer(({ graphId, onNodeSelect }) => {
  const [query, setQuery] = useState('')
  const [selectedNodeTypes, setSelectedNodeTypes] = useState<NodeType[]>([])
  const [searchResults, setSearchResults] = useState<GraphNode[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error('Please enter a search query')
      return
    }

    setIsSearching(true)

    try {
      const results = await knowledgeGraphStore.searchNodes(
        graphId,
        query,
        selectedNodeTypes.length > 0 ? selectedNodeTypes : undefined,
        20
      )
      setSearchResults(results)

      if (results.length === 0) {
        toast('No nodes found matching your query', { icon: 'ℹ️' })
      }
    } catch (error: any) {
      toast.error(error.message || 'Search failed')
    } finally {
      setIsSearching(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const toggleNodeType = (nodeType: NodeType) => {
    setSelectedNodeTypes((prev) =>
      prev.includes(nodeType)
        ? prev.filter((t) => t !== nodeType)
        : [...prev, nodeType]
    )
  }

  const clearSearch = () => {
    setQuery('')
    setSearchResults([])
    setSelectedNodeTypes([])
  }

  const handleNodeClick = (node: GraphNode) => {
    if (onNodeSelect) {
      onNodeSelect(node)
    }
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Search nodes by label or properties..."
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-ring focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={isSearching || !query.trim()}
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {isSearching ? 'Searching...' : 'Search'}
        </button>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
        >
          Filters
        </button>
        {(query || searchResults.length > 0) && (
          <button
            onClick={clearSearch}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Filter by Node Type</h4>
          <div className="flex flex-wrap gap-2">
            {Object.values(NodeType).map((nodeType) => (
              <button
                key={nodeType}
                onClick={() => toggleNodeType(nodeType)}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${
                  selectedNodeTypes.includes(nodeType)
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {nodeType}
              </button>
            ))}
          </div>
          {selectedNodeTypes.length > 0 && (
            <button
              onClick={() => setSelectedNodeTypes([])}
              className="mt-2 text-sm text-primary hover:text-primary"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-md divide-y divide-gray-200">
          <div className="px-4 py-2 bg-gray-50">
            <h4 className="text-sm font-medium text-gray-700">
              Found {searchResults.length} node{searchResults.length !== 1 ? 's' : ''}
            </h4>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {searchResults.map((node) => (
              <div
                key={node.id}
                onClick={() => handleNodeClick(node)}
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-primary uppercase">
                        {node.node_type}
                      </span>
                      <h5 className="text-sm font-medium text-gray-900">
                        {node.label}
                      </h5>
                    </div>
                    {node.properties && Object.keys(node.properties).length > 0 && (
                      <div className="mt-1 space-y-1">
                        {Object.entries(node.properties).slice(0, 3).map(([key, value]) => (
                          <div key={key} className="text-xs text-gray-600">
                            <span className="font-medium">{key}:</span> {String(value)}
                          </div>
                        ))}
                        {Object.keys(node.properties).length > 3 && (
                          <div className="text-xs text-gray-500">
                            +{Object.keys(node.properties).length - 3} more properties
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isSearching && query && searchResults.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <MagnifyingGlassIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
          <p>No nodes found matching "{query}"</p>
        </div>
      )}
    </div>
  )
})

GraphSearch.displayName = 'GraphSearch'

export default GraphSearch
